/**
 * @file TransferItemForm.tsx
 * @description Панель «Добавить строку» перемещения: фасовка → партия → куда →
 * сколько (phase19.23 этап B).
 *
 * Фасовка и партия подбираются по остаткам склада-источника: увезти можно
 * только то, что там лежит. «Откуда» показываем read-only — адрес партии
 * известен. Количество правится только при межскладском переезде: внутри
 * склада партия переезжает целиком (W14).
 *
 * phase19.24 этап 1, шаг 1: панель в общей карточке `DocumentAside`, первое
 * поле — скан артикула с «Подбором» (как в приёмке, списании и сборке), поля
 * строки — в `TransferItemFields`. Фокус в «Партию» переводит ЭФФЕКТ через ref
 * на сам триггер `Combobox`: колбэк поля звал `querySelector("button")` до
 * перерисовки, когда триггер был ещё `disabled`, и фокус улетал в `body`.
 * На `<lg` панель свёрнута до поля скана (`asideFieldsClass`).
 */

import { Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { TransferItemFields } from "./TransferItemFields";
import { TransferSkuField } from "./TransferSkuField";
import type { TransferTargetMode } from "./TransferTargetFields";
import type { TransferStockSku } from "./useTransferStockOptions";
import { Button } from "../../ui/button";
import { asideFieldsClass } from "../../ui/aside-fields";
import { DocumentAside } from "../../ui/DocumentAside";
import { ensureFieldsVisibleAboveBar } from "../../ui/sticky-bar";
import { ROW_HINT } from "../document-texts";
import { WmsFieldLabel } from "../WmsFieldLabel";
import type { StorageBinDto } from "../../../lib/api-bins";
import type { AddTransferItemPayload } from "../../../lib/api-transfers";
import type { WarehouseDto } from "../../../lib/api-wms";
import { validateQty } from "../../../lib/qty-input";
import { TRANSFER_QTY_UNIT } from "./TransferQuantityField";

interface Props {
  fromWarehouseId: string;
  /** Название склада-источника — подпись «Откуда». */
  fromWarehouseName: string;
  skus: TransferStockSku[];
  bins: StorageBinDto[];
  targetWarehouses: WarehouseDto[];
  /** Партии, уже стоящие в документе: одна партия — одна строка. */
  usedLotIds: string[];
  /** Фасовки, у которых в документе заняты все партии, — в «Подборе» серые. */
  fullyUsedSkuIds?: string[];
  /** Переход к уже добавленной строке документа. */
  onGoToExisting?: (skuId: string) => void;
  canMoveBetweenWarehouses: boolean;
  submitting: boolean;
  /** true — строка добавлена, форма сбрасывается. */
  onSubmit: (payload: AddTransferItemPayload) => Promise<boolean>;
}

export function TransferItemForm({
  fromWarehouseId,
  fromWarehouseName,
  skus,
  bins,
  targetWarehouses,
  usedLotIds,
  fullyUsedSkuIds = [],
  onGoToExisting,
  canMoveBetweenWarehouses,
  submitting,
  onSubmit,
}: Props) {
  const [skuId, setSkuId] = useState("");
  const [lotId, setLotId] = useState("");
  const [mode, setMode] = useState<TransferTargetMode>("BIN");
  const [binId, setBinId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [quantity, setQuantity] = useState("");
  // Ref на САМ триггер выбора партии: искать его в DOM было нельзя — в момент
  // распознавания артикула он ещё `disabled`.
  const lotTriggerRef = useRef<HTMLButtonElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);

  const sku = skus.find((s) => s.skuId === skuId) ?? null;
  // useMemo обязателен: новый массив на каждый рендер дёргал бы эффект сброса
  // партии бесконечно.
  const lots = useMemo(() => sku?.lots ?? [], [sku]);
  const lot = lots.find((l) => l.lotId === lotId) ?? null;

  // Фасовка выбрана — фокус уходит в «Партию»: следующий шаг формы.
  useEffect(() => {
    if (!sku) return;
    lotTriggerRef.current?.focus();
    // Раскрывшиеся поля на 360px упирались в полосу проведения (QA 2026-09-20).
    ensureFieldsVisibleAboveBar(fieldsRef.current, lotTriggerRef.current);
  }, [sku]);

  // Смена фасовки не должна утащить партию из прошлой карточки в запрос.
  useEffect(() => {
    setLotId((prev) => (lots.some((l) => l.lotId === prev) ? prev : ""));
  }, [lots]);

  // Количество по умолчанию — весь остаток партии: частичный переезд редкий.
  useEffect(() => {
    setQuantity(lot ? String(lot.quantity) : "");
  }, [lot]);

  // Дробную пачку и перебор остатка ловим до запроса (единица — см.
  // `TransferQuantityField`).
  const qtyError =
    mode === "BIN" || !quantity.trim()
      ? null
      : validateQty(quantity, TRANSFER_QTY_UNIT, {
          allowZero: false,
          max: lot?.quantity,
        });

  // Чего не хватает строке — одной фразой под кнопкой панели, в порядке полей.
  const targetMissing = mode === "BIN" ? !binId : !toWarehouseId;
  let hint: string | null = null;
  if (!sku) hint = ROW_HINT.sku;
  else if (!lot) hint = ROW_HINT.lot;
  else if (targetMissing) hint = ROW_HINT.target;
  else if (qtyError) hint = qtyError;
  const ready = !hint && !submitting;

  async function handleSubmit() {
    if (!sku || !lot || !ready) return;
    const ok = await onSubmit({
      skuId: sku.skuId,
      lotId: lot.lotId,
      toWarehouseId: mode === "BIN" ? fromWarehouseId : toWarehouseId,
      toBinId: mode === "BIN" ? binId : null,
      // Внутри склада количество не отправляем: сервер сам возьмёт всю партию.
      quantity: mode === "BIN" ? undefined : quantity.trim() || undefined,
    });
    if (!ok) return;
    // Сброс фасовки монтирует поле скана заново — оно само берёт фокус.
    setSkuId("");
    setLotId("");
    setBinId("");
    setQuantity("");
  }

  return (
    <DocumentAside
      title="Добавить строку"
      chrome="lg"
      titleClassName="hidden lg:flex"
      footerClassName={sku ? undefined : "hidden lg:flex"}
      footer={
        <>
          <Button onClick={() => void handleSubmit()} disabled={!ready}>
            <Plus className="size-4" />
            <span className="ml-1.5">Добавить строку</span>
          </Button>
          {hint ? <p className="text-xs text-on-surface-variant">{hint}</p> : null}
        </>
      }
    >
      <WmsFieldLabel label="Фасовка (артикул или скан)" className="hidden lg:block">
        <TransferSkuField
          skus={skus}
          value={sku}
          nextFieldRef={lotTriggerRef}
          onChange={(next) => setSkuId(next?.skuId ?? "")}
          alreadyAddedIds={fullyUsedSkuIds}
          onGoToExisting={onGoToExisting}
          disabled={submitting}
        />
      </WmsFieldLabel>

      <div ref={fieldsRef} className={asideFieldsClass(!!sku)}>
        <TransferItemFields
          lots={lots}
          usedLotIds={usedLotIds}
          lotId={lotId}
          onLotChange={setLotId}
          lotTriggerRef={lotTriggerRef}
          lot={lot}
          hasSku={!!sku}
          fromWarehouseName={fromWarehouseName}
          mode={mode}
          onModeChange={setMode}
          binId={binId}
          onBinChange={setBinId}
          toWarehouseId={toWarehouseId}
          onWarehouseChange={setToWarehouseId}
          bins={bins}
          fromWarehouseId={fromWarehouseId}
          targetWarehouses={targetWarehouses}
          canMoveBetweenWarehouses={canMoveBetweenWarehouses}
          quantity={quantity}
          onQuantityChange={setQuantity}
          qtyError={qtyError}
          disabled={submitting}
        />
      </div>
    </DocumentAside>
  );
}
